import { cn } from '@/lib/utils';
import type { ViewPreset } from '../engine/types';
import { FloatingPanel } from './FloatingPanel';

/**
 * The fixed viewpoints, and a jump to each tooth.
 *
 * Checking a margin is a sequence of the same views every time - buccal,
 * lingual, occlusal - and orbiting to each by hand with a trackpad is slow and
 * never quite square. With several teeth in a case, the tooth chips re-centre
 * the rotation on the one being judged.
 */
const PRESETS: [ViewPreset, string][] = [
  ['front', 'Front'],
  ['back', 'Back'],
  ['left', 'Left'],
  ['right', 'Right'],
  ['top', 'Top'],
  ['bottom', 'Bottom'],
  ['iso', 'Iso'],
];

interface ViewPresetsProps {
  teeth: string[];
  focusedTooth: string | null;
  onView: (preset: ViewPreset) => void;
  onFocusTooth: (tooth: string | null) => void;
  compact?: boolean;
}

export function ViewPresets({
  teeth,
  focusedTooth,
  onView,
  onFocusTooth,
  compact = false,
}: ViewPresetsProps) {
  return (
    <div className="flex flex-col items-start gap-2">
      <FloatingPanel role="group" aria-label="Camera views" className="flex flex-wrap gap-0.5 p-1">
        {PRESETS.filter(([preset]) => !compact || preset !== 'bottom').map(([preset, label]) => (
          <button
            key={preset}
            type="button"
            onClick={() => {
              onView(preset);
            }}
            className="h-7 rounded-md px-2.5 text-[12px] font-medium text-neutral-600 transition-colors hover:bg-neutral-900/5 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/10 dark:hover:text-white"
          >
            {label}
          </button>
        ))}
      </FloatingPanel>

      {teeth.length > 1 && (
        <FloatingPanel
          role="group"
          aria-label="Centre on a tooth"
          className="flex items-center gap-0.5 p-1"
        >
          <span className="px-2 text-[11px] font-medium tracking-wide text-neutral-500 uppercase">
            Tooth
          </span>
          {teeth.map((tooth) => (
            <button
              key={tooth}
              type="button"
              aria-pressed={focusedTooth === tooth}
              onClick={() => {
                onFocusTooth(tooth);
              }}
              className={cn(
                'h-7 min-w-9 rounded-md px-2 font-mono text-[12px] font-semibold transition-colors',
                focusedTooth === tooth
                  ? 'bg-primary text-primary-foreground'
                  : 'text-neutral-700 hover:bg-neutral-900/5 dark:text-neutral-200 dark:hover:bg-white/10',
              )}
            >
              {tooth}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={focusedTooth === null}
            onClick={() => {
              onFocusTooth(null);
            }}
            className={cn(
              'h-7 rounded-md px-2 text-[12px] font-medium transition-colors',
              focusedTooth === null
                ? 'bg-primary text-primary-foreground'
                : 'text-neutral-600 hover:bg-neutral-900/5 dark:text-neutral-300 dark:hover:bg-white/10',
            )}
          >
            All
          </button>
        </FloatingPanel>
      )}
    </div>
  );
}
